import {useMemo} from 'react';
import PropTypes from 'prop-types';
import {useLocation, useNavigate} from 'react-router-dom';
import {
  BackButton,
  ButtonWithIcon,
  ProgressIndicator
} from '@/components/atoms';
import {getPerfectStoreStepConfig} from '@/utils/perfectStoreStepConfig';
import {Accordion} from '@/components/molecules/Accordion';
import {AccordionItem} from '@/components/molecules/AccordionItem';
import {PageHeader, SaveCancelFooter} from '@/components/molecules';
import {
  PepIrisDisplayReview,
  PepIrisSetupReview
} from '@/containers/PerfectStore/PerfectStoreReview';
import {useAppSelector} from '@/store';
import {selectCountryCode} from '@/store/slices/countryCodeSlice';
import {COUNTRY_LANGUAGES_CODES} from '@/utils/constants/globalConstants';
import {ROUTES} from '@/utils/constants/routesConstants';
import {
  getPerfectStoreCancelModal,
  isPepIrisDisplayInvalid
} from '@/pages/PerfectStoreUtils';
import {CREATE_PERFECT_STORE_TITLE} from '@/utils/constants/perfectStoreConstants';
import {usePerfectStoreReviewActions} from '@/hooks/usePerfectStoreReviewActions';
import '@/containers/PerfectStore/PerfectStoreReview/perfectStoreReview.scss';

const stepConfig = getPerfectStoreStepConfig();

const EditSectionButton = ({step, onEdit}) => (
  <ButtonWithIcon
    iconLeading='edit'
    handleButton={(event) => {
      event?.stopPropagation();
      onEdit(step);
    }}
    uiSchema={{
      title: 'Edit Section',
      variant: 'tertiary',
      size: 'small',
      isDark: true
    }}
  />
);

EditSectionButton.propTypes = {
  step: PropTypes.number.isRequired,
  onEdit: PropTypes.func.isRequired
};

export const PepIrisReview = () => {
  const location = useLocation();
  const disableDraft = location?.state?.disableDraft ?? false;
  const navigate = useNavigate();
  const formData = useAppSelector((state) => state?.perfectStore?.formData);
  const countryCode = useAppSelector(selectCountryCode);
  const commonLanguages = useAppSelector((state) => state.common?.languages);
  const fromWizard = location?.state?.fromWizard;

  const languages = useMemo(
    () => COUNTRY_LANGUAGES_CODES[countryCode] || commonLanguages || [],
    [countryCode, commonLanguages]
  );

  const getNavigationState = () => ({
    isEdit: true,
    disableDraft,
    fromWizard: true,
    ...(location.pathname ? {from: location.pathname} : {})
  });

  const handleStepClick = (step) => {
    const stepRoutes = {
      1: ROUTES.PERFECT_STORE_ADD,
      2: ROUTES.PERFECT_STORE_PEPIRIS_DISPLAY
    };
    if (stepRoutes[step]) {
      navigate(stepRoutes[step], {state: getNavigationState()});
    }
  };

  const {
    isCancel,
    handleCancel,
    onClickPrimaryButton,
    handleDraftAction,
    handlePublishAction,
    publishDisabled
  } = usePerfectStoreReviewActions({
    formData,
    languages,
    isDisplayInvalid: isPepIrisDisplayInvalid
  });

  return (
    <>
      <div
        className='perfect-store-detail-page'
        data-testid='pepiris-review-page'>
        <BackButton id='pepiris-review-back' />
        <PageHeader title={CREATE_PERFECT_STORE_TITLE} />

        {fromWizard && (
          <ProgressIndicator
            currentStep={3}
            completed={false}
            orientation='horizontal'
            steps={stepConfig.steps}
            titles={stepConfig.titles}
            className='mb-6'
            onStepClick={handleStepClick}
          />
        )}

        <div
          className='perfect-store-review flex flex-col gap-4 pt-4'
          data-testid='pepiris-review-wrapper'>
          <Accordion
            content={
              <AccordionItem
                expanded
                label='Setup'
                rightAccessoryChild={
                  <EditSectionButton step={1} onEdit={handleStepClick} />
                }
                content={<PepIrisSetupReview formData={formData} />}
              />
            }
          />
          <Accordion
            content={
              <AccordionItem
                expanded
                label='Display'
                rightAccessoryChild={
                  <EditSectionButton step={2} onEdit={handleStepClick} />
                }
                content={
                  <PepIrisDisplayReview
                    formData={formData}
                    languages={languages}
                  />
                }
              />
            }
          />
        </div>

        <SaveCancelFooter
          FormError={publishDisabled}
          handleCancel={handleCancel}
          draftAction={handleDraftAction}
          nextAction={handlePublishAction}
          hideSaveDraft={false}
          disableDraft={disableDraft}
          isReview
        />
      </div>
      {isCancel &&
        getPerfectStoreCancelModal({
          show: isCancel,
          onConfirm: onClickPrimaryButton,
          onClose: handleCancel
        })}
    </>
  );
};
